// React
import React from 'react';

// Components
import { MainCard } from 'components';

// Packages
import { Alert, Grid, Stack, Box, Button } from '@mui/material';
import { capitalizeFirstLetterMulti } from '../../utils/Helpers';

const AMBER = '#fcba31';
const SEAGREEN = '#33C78F';

const RegistrationComplete = ({
    registrationOrgName,
    resetForm,
    registrationResult,
    onRetryFailed
}) => {
    const successCount = registrationResult?.succeeded?.length || 0;
    const failedCount = registrationResult?.failed?.length || 0;

    const hasFailures = failedCount > 0;
    const titleColor = hasFailures ? AMBER : SEAGREEN;
    const occupantWord = successCount === 1 ? 'occupant' : 'occupants';

    const failedNames =
        registrationResult?.failed?.map(f =>
            capitalizeFirstLetterMulti(`${f.occupant.first_name} ${f.occupant.last_name}`)
        ) || [];

    const renderFailedNames = () => {
        if (failedNames.length === 1) {
            return (
                <span style={{ color: AMBER, fontWeight: 600 }}>{failedNames[0]}</span>
            );
        }
        return failedNames.map((name, i) => {
            const isLast = i === failedNames.length - 1;
            const isSecondToLast = i === failedNames.length - 2;
            return (
                <React.Fragment key={`${name}-${i}`}>
                    <span style={{ color: AMBER, fontWeight: 600 }}>{name}</span>
                    {!isLast && (isSecondToLast ? ' and ' : ', ')}
                </React.Fragment>
            );
        });
    };

    return (
        <Grid
            item
            sm={8}
            sx={{
                backgroundColor: 'rgba(33, 35, 34, 0.05)',
                height: '100vh',
                padding: '40px',
                boxSizing: 'border-box',
                overflowY: 'auto',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'safe center'
            }}
        >
            <MainCard
                style={{ padding: '10px 30px', fontSize: '1rem', lineHeight: '1.6' }}
                className={'oeThanks'}
            >
                <Box>
                    <Stack
                        spacing={3}
                        sx={{ justifyContent: 'center', alignItems: 'center' }}
                    >
                        <h2 style={{ color: titleColor, margin: 0, textAlign: 'center' }}>
                            {hasFailures
                                ? 'Enrollment Partially Completed'
                                : 'Thank You for Enrolling!'}
                        </h2>

                        <p style={{ margin: 0, textAlign: 'center' }}>
                            Enrollment request for{' '}
                            <span className="oeSpan">{registrationOrgName}</span> has been
                            submitted successfully.
                        </p>

                        <p style={{ margin: 0, textAlign: 'center' }}>
                            <span className="oeSpan">
                                {successCount} {occupantWord}
                            </span>
                            {' '}are now pending approval by the community management team.
                        </p>

                        <div className="oeSteps" style={{ width: '100%' }}>
                            <Stack
                                direction="row"
                                spacing={3}
                                alignItems="center"
                                sx={{ marginBottom: '24px' }}
                            >
                                <div className="oeNumberBox">
                                    <span>1</span>
                                </div>
                                <div className="text">
                                    <span className="oeSpan">Verification:</span> Your
                                    community management team will verify your
                                    information.
                                </div>
                            </Stack>
                            <Stack
                                direction="row"
                                spacing={3}
                                alignItems="center"
                                sx={{ marginBottom: '24px' }}
                            >
                                <div className="oeNumberBox">
                                    <span>2</span>
                                </div>
                                <div className="text">
                                    <span className="oeSpan">Invite:</span> Once verified,
                                    you’ll receive a text to activate your account.
                                </div>
                            </Stack>
                            <Stack direction="row" spacing={3} alignItems="center">
                                <div className="oeNumberBox">
                                    <span>3</span>
                                </div>
                                <div className="text">
                                    <span className="oeSpan">Get Started:</span> Use the
                                    SAGE app to access all your community benefits!
                                </div>
                            </Stack>
                        </div>
                    </Stack>
                </Box>
                <Stack spacing={1.5} sx={{ marginTop: '24px' }}>
                    <Button
                        variant="contained"
                        className="oeOccupant"
                        size="large"
                        onClick={() => {
                            resetForm('hard');
                        }}
                    >
                        New Enrollment
                    </Button>
                    {hasFailures && (
                        <>
                            <Alert
                                severity="warning"
                                variant="outlined"
                                sx={{
                                    width: '100%',
                                    mt: '50px !important',
                                    color: '#fff',
                                    borderColor: 'rgba(252, 186, 49, 0.5)',
                                    backgroundColor: 'rgba(252, 186, 49, 0.08)',
                                    alignItems: 'center',
                                    '& .MuiAlert-icon': { color: AMBER },
                                    '& .MuiAlert-message': {
                                        padding: '4px 0',
                                        maxHeight: 200,
                                        overflowY: 'auto',
                                        wordBreak: 'break-word'
                                    }
                                }}
                            >
                                Something went wrong creating enrollment requests for{' '}
                                {renderFailedNames()}. Please try again.
                            </Alert>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={onRetryFailed}
                                sx={{
                                    backgroundColor: AMBER,
                                    color: '#212322',
                                    '&:hover': { backgroundColor: '#edb23b' }
                                }}
                            >
                                Try Again for Failed{' '}
                                {failedCount === 1 ? 'Enrollment' : 'Enrollments'}
                            </Button>
                        </>
                    )}
                </Stack>
            </MainCard>
        </Grid>
    );
};

export default RegistrationComplete;
